import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  MediaType,
  useCreateMutation,
  useCurrentWorkspace,
  useUpdateMutation,
  useWorkspaceAsset,
  useWorkspaceCategories,
} from '@gripp/shared-logic';
import {
  AddLink,
  Button,
  CategorySelector,
  ControlledTextInput,
  Spacing,
  tokens,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import * as yup from 'yup';
import { object as yupObject } from 'yup';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { useMediaUpload } from '../../shared/hooks/useMediaUpload';
import { AddAssetMediaType } from './addFilesLinksButton';

const CREATE_MEDIA = `
  mutation createMedia($input: CreateMediaInput!) {
    createMedia(input: $input) {
      id
    }
  }
`;

const CREATE_CATEGORY = `
  mutation createCategory($input: CreateCategoryInput!) {
    createCategory(input: $input) {
      id
      name
    }
  }
`;

const UPDATE_ASSET = `
  mutation UpdateAsset($input: MutateAssetInput!) {
    updateAsset(input: $input) {
      id
    }
  }
`;

/**
 * Delay before the file picker auto-opens. The panel slide-in transition runs
 * ~380ms (300ms animation + 80ms buffer); opening the OS dialog sooner pops it
 * over the still-sliding previous panel instead of the settled form.
 */
const PICKER_AUTO_OPEN_DELAY_MS = 500;

type CategoryItem = { id: string; name: string };

type AssetFileCreateFormProps = {
  type: AddAssetMediaType;
  assetId: string;
  /** Existing media ids on the asset — the new file is appended to these. */
  allFileIds: string[];
  onComplete: () => void;
  onCategoryEdit?: () => void;
  selectedCategories?: CategoryItem[];
  onCategoriesChange?: (cats: CategoryItem[]) => void;
};

const buildSchema = (isLink: boolean) =>
  yupObject({
    title: yup.string().required(),
    linkUrl: isLink ? yup.string().url().required() : yup.string().optional(),
    categories: yup
      .array()
      .of(
        yupObject({
          id: yup.string().required(),
          name: yup.string().required(),
        })
      )
      .optional(),
  });

/**
 * Panel form for adding a file or link to an asset. Mirrors `AssetFileEditForm`
 * (the edit-side panel) but creates new media instead of updating it — this is
 * the panelized replacement for the old `CreateFileForm` Modal.
 */
export const AssetFileCreateForm = ({
  type,
  assetId,
  allFileIds,
  onComplete,
  onCategoryEdit,
  selectedCategories: externalCategories,
  onCategoriesChange,
}: AssetFileCreateFormProps) => {
  const { t } = useTranslation();
  const isLink = type === 'link';
  const currentWorkspace = useCurrentWorkspace();
  const { categories: availableCategories } = useWorkspaceCategories();
  const { invalidateQuery } = useWorkspaceAsset(assetId);
  const slideLayout = useSlideLayoutSafe();

  const { mutateAsync: createMedia, isPending: isCreatingMedia } =
    useCreateMutation({ modelName: 'media', query: CREATE_MEDIA });
  const { mutateAsync: createCategory } = useCreateMutation({
    modelName: 'category',
    query: CREATE_CATEGORY,
  });
  const { mutateAsync: updateAsset } = useUpdateMutation({
    modelName: 'asset',
    query: UPDATE_ASSET,
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const localUrlRef = useRef<string | null>(null);

  const uploadMedia = useMediaUpload({
    onStart: () => setIsUploading(true),
    onComplete: () => setIsUploading(false),
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid },
  } = useForm({
    resolver: yupResolver(buildSchema(isLink)),
    mode: 'onChange',
    defaultValues: { title: '', linkUrl: '', categories: [] as CategoryItem[] },
  });

  const selectedCategories =
    (watch('categories') as CategoryItem[] | undefined) ?? [];

  // Sync categories chosen in the third-column EditCategory panel back into the form.
  useEffect(() => {
    if (!externalCategories) return;
    const formIds = selectedCategories
      .map((c) => c.id)
      .sort()
      .join(',');
    const extIds = externalCategories
      .map((c) => c.id)
      .sort()
      .join(',');
    if (formIds !== extIds) {
      setValue('categories', externalCategories, { shouldDirty: true });
    }
  }, [externalCategories]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      if (localUrlRef.current) {
        URL.revokeObjectURL(localUrlRef.current);
        localUrlRef.current = null;
      }
    },
    []
  );

  const handleFileSelected = (file: File) => {
    if (localUrlRef.current) URL.revokeObjectURL(localUrlRef.current);
    localUrlRef.current = URL.createObjectURL(file);
    setSelectedFile(file);
    setErrorMessage(null);
    setValue('title', file.name, { shouldDirty: true, shouldValidate: true });
  };

  const handleFileRemove = () => {
    if (localUrlRef.current) {
      URL.revokeObjectURL(localUrlRef.current);
      localUrlRef.current = null;
    }
    setSelectedFile(null);
    setValue('title', '', { shouldDirty: true, shouldValidate: true });
  };

  const handleCreateCategory = useCallback(
    async (name: string) => {
      if (!currentWorkspace?.id) {
        throw new Error('Workspace is required to create a category.');
      }
      const result = await createCategory({
        input: { name: name.trim(), workspace: { id: currentWorkspace.id } },
      });
      return { id: result.id, name: result.name };
    },
    [createCategory, currentWorkspace?.id]
  );

  const canSave =
    isValid && !isUploading && !isCreatingMedia && (isLink || !!selectedFile);

  const onSubmit = async (data: {
    title: string;
    linkUrl?: string;
    categories?: CategoryItem[];
  }) => {
    if (!currentWorkspace?.id) return;
    const categories = (data.categories ?? []).map((c) => ({ id: c.id }));

    try {
      let newMediaId: string | undefined;

      if (isLink) {
        const result = await createMedia({
          input: {
            name: data.title,
            type: MediaType.Link,
            extension: 'link',
            status: 'PROCESSED',
            url: data.linkUrl,
            workspaces: [{ id: currentWorkspace.id }],
            categories,
          },
        });
        newMediaId = result?.id;
      } else {
        if (!selectedFile) return;
        newMediaId = await uploadMedia(
          selectedFile,
          currentWorkspace.id,
          data.title,
          categories
        );
      }

      if (!newMediaId) {
        setErrorMessage(
          t(isLink ? 'file.create.linkError' : 'file.create.uploadError')
        );
        return;
      }

      const media = [...allFileIds, newMediaId].map((id) => ({ id }));
      await updateAsset({ input: { id: assetId, media } });
      await invalidateQuery();
      onComplete();
    } catch (error) {
      console.error('Failed to add file to asset:', error);
      setErrorMessage(
        t(isLink ? 'file.create.linkError' : 'file.create.uploadError')
      );
    }
  };

  return (
    <ScrollView
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      {isLink ? (
        <AddLink name="linkUrl" control={control} label="asset.files.link" />
      ) : (
        <View style={styles.fileField}>
          {selectedFile ? (
            <View style={styles.filePreview}>
              <View style={styles.fileImageContainer}>
                {selectedFile.type.startsWith('image/') &&
                localUrlRef.current ? (
                  <img
                    src={localUrlRef.current}
                    alt="File preview"
                    style={styles.fileImage}
                  />
                ) : (
                  <MaterialCommunityIcons
                    name="file-document"
                    size={28}
                    color={tokens.colors.brand.black}
                  />
                )}
              </View>
              <Pressable
                style={styles.closeButton}
                onPress={handleFileRemove}
                testID="asset-file-create-remove-file"
              >
                <MaterialCommunityIcons
                  name="close-circle"
                  size={20}
                  color={tokens.colors.neutral[600]}
                />
              </Pressable>
            </View>
          ) : (
            <MediaPicker
              autoUpload={false}
              autoTrigger
              autoTriggerDelay={PICKER_AUTO_OPEN_DELAY_MS}
              onFileSelected={handleFileSelected}
              setMedia={() => undefined}
              setProgress={() => undefined}
              setUploading={setIsUploading}
              acceptedMediaTypes={[
                MediaType.Image,
                MediaType.Document,
                MediaType.Video,
              ]}
              workspaceId={currentWorkspace?.id}
              customTrigger={
                <View style={styles.fileUploadArea}>
                  <MaterialCommunityIcons
                    name="plus"
                    size={24}
                    color={tokens.colors.brand.black}
                  />
                </View>
              }
            />
          )}
        </View>
      )}

      <View>
        <Text style={styles.label}>{t('asset.files.name')}</Text>
        <ControlledTextInput
          control={control}
          name="title"
          placeholder={t('asset.files.name')}
          useCropCircle
          hideBorder
        />
      </View>

      <CategorySelector
        selectedCategories={selectedCategories}
        availableCategories={availableCategories ?? []}
        onCategoriesChange={(cats) => {
          setValue('categories', cats, { shouldDirty: true });
          onCategoriesChange?.(cats);
        }}
        onCreateCategory={handleCreateCategory}
        useCropCircle
        onSelectorEdit={onCategoryEdit ?? slideLayout?.toggleThirdPanel}
      />

      {errorMessage && <Text style={styles.errorText}>{errorMessage}</Text>}

      <View style={styles.contentActions}>
        <Button
          testID="asset-file-create-save-button"
          style={styles.saveButton}
          onPress={canSave ? handleSubmit(onSubmit) : undefined}
          state={canSave ? 'active' : 'default'}
          disabled={!canSave}
          size="large"
        >
          {t('file.create.add')}
        </Button>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    padding: Spacing.basePadding.base,
    gap: 14,
  },
  fileField: {
    alignItems: 'flex-start',
  },
  label: {
    ...tokens.typography.body.small,
    fontWeight: '600',
    marginBottom: 8,
    color: tokens.colors.brand.black,
  },
  fileUploadArea: {
    width: 80,
    height: 80,
    borderRadius: 8,
    backgroundColor: tokens.colors.slate[100],
    alignItems: 'center',
    justifyContent: 'center',
  },
  filePreview: {
    position: 'relative',
    width: 80,
    height: 80,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: tokens.colors.slate[100],
  },
  fileImageContainer: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 1,
  },
  errorText: {
    ...tokens.typography.body.small,
    color: tokens.colors.semantic.error,
  },
  contentActions: {
    marginTop: 'auto',
    paddingTop: Spacing.basePadding.base * 2,
  },
  saveButton: {
    borderRadius: 2,
    width: '100%',
    height: 48,
  },
});
